/* islandSnap.js — CAD adjacency + alignment for FREE-STANDING (island) units
 * (2026-07-14 precision mandate, Issue 1/2/3).
 *
 * Free placements previously had NO snapping at all: islands were dropped at
 * the raw tap point and could never be nudged into perfect 0mm contact with a
 * neighbour ("island creation is broken"). This module is the pure magnet:
 *
 *   snapIslandXZ(p, def, x, z, placements, lib, walls[, tolM])
 *     → { x, z, guides }
 *
 * Per world axis it finds the nearest snap candidate within tolM and moves the
 * island centre onto it EXACTLY (kind priority on ties: contact > flush >
 * center — the professional default):
 *   contact — my face touches their face (0mm gap; only when the two units
 *             overlap vertically, i.e. genuinely stand beside each other)
 *   flush   — same-side faces align (run fronts/backs line up)
 *   center  — centres align
 *
 * guides: [{ axis:'x'|'z', kind, at, otherId }] — `at` is the world coordinate
 * of the alignment line (x for axis 'x', z for axis 'z') so the view can draw
 * the green guide exactly through the matched faces.
 *
 * Geometry is world-AABB-based: exact for the axis-aligned islands the
 * placement pipeline produces (orientation inherited from the nearest wall of
 * a rectilinear room). Rotated neighbours degrade gracefully — their AABB
 * envelope still gives a useful, conservative snap line.
 */
import { placementAABB } from "./placement";
import { lhcVOverlap } from "./collision";
import { effDef } from "./pricing";

export const ISLAND_SNAP_TOL_M = 0.03;  // 30mm magnet reach (world)
const RANK = { contact: 0, flush: 1, center: 2 };

export function snapIslandXZ(p, def, x, z, placements, lib, walls, tolM = ISLAND_SNAP_TOL_M) {
  const probe = { ...p, island: true, x, z };
  const box = def ? placementAABB(probe, def, walls) : null;
  if (!box) return { x, z, guides: [] };
  const hx = (box.maxX - box.minX) / 2, hz = (box.maxZ - box.minZ) / 2;
  /* SIDE PANELS (2026-09-07): the box centre is offset from the island's pose
   * when a board hangs off one side — magnet in BOX space, return POSE space,
   * so the panel (not the carcass) is what touches the neighbour. */
  const offX = box.cx - x, offZ = box.cz - z;

  let bestX = null, bestZ = null;
  const consider = (axis, center, lineAt, kind, other) => {
    const cur = axis === "x" ? x + offX : z + offZ;
    const d = Math.abs(center - cur);
    if (d > tolM) return;
    const slot = axis === "x" ? bestX : bestZ;
    const better = !slot || d < slot.d - 1e-9 ||
      (Math.abs(d - slot.d) <= 1e-9 && RANK[kind] < RANK[slot.kind]);
    if (!better) return;
    const rec = { d, center, at: lineAt, kind, otherId: other.id };
    if (axis === "x") bestX = rec; else bestZ = rec;
  };

  (placements || []).forEach((q) => {
    if (!q || q.id === p.id) return;
    const qd = effDef(q, lib);
    if (!qd) return;
    const qb = placementAABB(q, qd, walls);
    if (!qb) return;
    const beside = lhcVOverlap(probe, q, lib); // same vertical zone → contact is meaningful
    if (beside) {
      consider("x", qb.maxX + hx, qb.maxX, "contact", q);
      consider("x", qb.minX - hx, qb.minX, "contact", q);
      consider("z", qb.maxZ + hz, qb.maxZ, "contact", q);
      consider("z", qb.minZ - hz, qb.minZ, "contact", q);
    }
    consider("x", qb.minX + hx, qb.minX, "flush", q);
    consider("x", qb.maxX - hx, qb.maxX, "flush", q);
    consider("x", (qb.minX + qb.maxX) / 2, (qb.minX + qb.maxX) / 2, "center", q);
    consider("z", qb.minZ + hz, qb.minZ, "flush", q);
    consider("z", qb.maxZ - hz, qb.maxZ, "flush", q);
    consider("z", (qb.minZ + qb.maxZ) / 2, (qb.minZ + qb.maxZ) / 2, "center", q);
  });

  const guides = [];
  let nx = x, nz = z;
  if (bestX) { nx = bestX.center - offX; guides.push({ axis: "x", kind: bestX.kind, at: bestX.at, otherId: bestX.otherId }); }
  if (bestZ) { nz = bestZ.center - offZ; guides.push({ axis: "z", kind: bestZ.kind, at: bestZ.at, otherId: bestZ.otherId }); }
  return { x: nx, z: nz, guides };
}

/** nearestIslandNeighbor(x, z, placements, lib, walls[, maxDist]): the island
 *  placement whose box is closest to the point (≤ maxDist), else null. Used to
 *  let a drop AWAY from every wall join an existing island from ANY side —
 *  the far side used to die on the B2 wall-distance refusal ("bottom refuses
 *  placement"). */
export function nearestIslandNeighbor(x, z, placements, lib, walls, maxDist = 0.6) {
  let best = null, bestD = maxDist;
  (placements || []).forEach((q) => {
    if (!q || !q.island) return;
    const qd = effDef(q, lib);
    if (!qd) return;
    const b = placementAABB(q, qd, walls);
    if (!b) return;
    const dx = Math.max(b.minX - x, 0, x - b.maxX);
    const dz = Math.max(b.minZ - z, 0, z - b.maxZ);
    const d = Math.hypot(dx, dz);
    if (d <= bestD) { bestD = d; best = q; }
  });
  return best;
}

/** resolveIslandContact(p, def, placements, lib, walls): if the island probe
 *  OVERLAPS a neighbour (cursor dropped ON a run), push it out along the axis
 *  of least penetration until the faces touch exactly — the CAD "place beside
 *  what I aimed at" resolution. Mutates p.x / p.z. Iterates a few times so a
 *  corner push against two neighbours settles. */
export function resolveIslandContact(p, def, placements, lib, walls, maxIter = 4) {
  for (let it = 0; it < maxIter; it++) {
    const box = def ? placementAABB(p, def, walls) : null;
    if (!box) return;
    let pushed = false;
    for (const q of placements || []) {
      if (!q || q.id === p.id) continue;
      const qd = effDef(q, lib);
      if (!qd) continue;
      if (!lhcVOverlap(p, q, lib)) continue;
      const qb = placementAABB(q, qd, walls);
      if (!qb) continue;
      const ox = Math.min(box.maxX, qb.maxX) - Math.max(box.minX, qb.minX);
      const oz = Math.min(box.maxZ, qb.maxZ) - Math.max(box.minZ, qb.minZ);
      if (ox <= 0.0005 || oz <= 0.0005) continue;      // touching / clear
      if (ox <= oz) p.x += box.cx < qb.cx ? -ox : ox;  // least-penetration axis
      else p.z += box.cz < qb.cz ? -oz : oz;
      pushed = true;
      break;                                            // recompute and re-scan
    }
    if (!pushed) return;
  }
}
